import React, { useMemo, useState } from "react";
import { NavLink, Route, Switch, useLocation } from "react-router-dom";
import { AvatarDropdown, Notification, Redirect, RequiresAuth } from "../components";
import { Col, Layout, Menu, Row } from "antd";
import { FolderOutlined, HomeOutlined, MenuFoldOutlined, MenuUnfoldOutlined, SettingOutlined, TableOutlined } from "@ant-design/icons";
import { shallowEqual, useSelector } from "react-redux";
import _ from 'lodash';
import { HomePage, MiFIROperationalPage, MiFIRErrorsPage, MiFIRProcessStatusPage, Settings, TableDataPage } from "./index";

const { SubMenu } = Menu;
const { Header, Content, Sider, Footer } = Layout;

export default () => (
  <Switch>
    <RequiresAuth path="/">
      <AuthenticatedMainPage />
    </RequiresAuth>
  </Switch>
)

const AuthenticatedMainPage = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const root = "/" + location.pathname.split("/")[1];
  const auth = useSelector(state => state.auth, shallowEqual);
  const config = useSelector(state => state.config, shallowEqual);

  const regulationsById = useMemo(() => _.mapValues(_.keyBy(config.regulations, 'id'), v => v.label), [config.regulations]);
  const menuItems = useMemo(() => [
    {
      path: "/",
      title: "Home",
      icon: <HomeOutlined />,
      component: HomePage,
    },
    ...(auth.role === 'admin' ? [{
      path: "/settings",
      title: "Settings",
      icon: <SettingOutlined />,
      component: <Settings />,
    }] : []),
    ...(auth.regulation.flatMap(rId => {
      const rLabel = regulationsById[rId];
      if (!rLabel) return [];
      const tables = _.orderBy(_.filter(config.tables, t => _.some(t.regulations, r => r === rId)), t => t.order, 'asc');
      if (!tables || !tables.length) return [];
      return [{
        path: `/tables/${rId}`,
        title: rLabel,
        icon: <FolderOutlined />,
        menuClassName: "regulation-parent",
        submenu: [
          ...(rLabel === 'MiFIR' ? [{
            path: `/tables/${rId}/mifir-daily-reporting`,
            title: "Daily Reporting",
            icon: <FolderOutlined />,
            submenu: [{
              path: `/tables/${rId}/mifir-transactions-not-submitted`,
              title: "Transactions Not Submitted",
              icon: <TableOutlined />,
              component: <MiFIROperationalPage page="transactionsNotSubmitted" />
            }, {
              path: `/tables/${rId}/mifir-feedback`,
              title: "Feedback",
              icon: <TableOutlined />,
              component: <MiFIROperationalPage page="feedback" />
            }, {
              path: `/tables/${rId}/mifir-errors`,
              title: "Errors",
              icon: <TableOutlined />,
              component: <MiFIRErrorsPage />
            }, {
              path: `/tables/${rId}/mifir-submissions`,
              title: "Submissions",
              icon: <TableOutlined />,
              component: <MiFIROperationalPage page="submissions" />
            }, {
              path: `/tables/${rId}/mifir-process-status`,
              title: "Process Status",
              icon: <TableOutlined />,
              component: <MiFIRProcessStatusPage />
            }]
          }]: []),
          ...tables.map(t => ({
            path: `/tables/${rId}/${t.name}`,
            title: t.label,
            icon: <TableOutlined />,
            component: <TableDataPage />
          }))
        ]
      }]
    }))
  ], [auth, regulationsById, config.tables]);

  const rootSubmenuKeys = menuItems.map(i => i.path)
  const initialOpenKeys = useMemo(() => {
    let bestMatch = { size: 0, ancestors: [] };

    const visit = (items, ancestors = []) => {
      items.forEach(item => {
        if (item.submenu) {
          visit(item.submenu, [...ancestors, item.path]);
          return;
        }

        if (_.includes(location.pathname, item.path) && item.path.length > bestMatch.size) {
          bestMatch = { size: item.path.length, ancestors };
        }
      });
    };

    visit(menuItems);
    return bestMatch.ancestors;
  }, [menuItems, location.pathname]);
  const [openKeys, setOpenKeys] = useState(initialOpenKeys);

  const onOpenChange = keys => {
    const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
    if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  return (
    <React.Fragment>
      <Layout id="mainPageLayout">
        <Sider trigger={null} collapsible collapsed={collapsed} width={225} collapsedWidth={80}>
          <div className="logo">
            <img src="/finmatek.png" alt="Finmatek Logo" />
          </div>
          <Menu
            id="topMenu"
            theme="dark"
            mode="inline"
            defaultOpenKeys={[root]}
            selectedKeys={[location.pathname]}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
          >
            {menuItems.map(renderMenuItem)}
          </Menu>
        </Sider>
        <Layout>
          <Header id="topHeader">
            <Row>
              <Col span={18}>
                {collapsed ? (
                  <MenuUnfoldOutlined
                    className="headerMenu-IconBurger"
                    onClick={() => setCollapsed(!collapsed)}
                  />
                ) : (
                  <MenuFoldOutlined
                    className="headerMenu-IconBurger"
                    onClick={() => setCollapsed(!collapsed)}
                  />
                )}
              </Col>
              <Col span={6}>
                <div id="topHeaderRightElements" className="right">
                  <AvatarDropdown />
                </div>
              </Col>
            </Row>
          </Header>
          <Content id="mainPageContent">
            <Switch>
              <Route path="/tables/:regulation/mifir-transactions-not-submitted">
                <MiFIROperationalPage page="transactionsNotSubmitted" />
              </Route>
              <Route path="/tables/:regulation/mifir-feedback">
                <MiFIROperationalPage page="feedback" />
              </Route>
              <Route path="/tables/:regulation/mifir-errors">
                <MiFIRErrorsPage />
              </Route>
              <Route path="/tables/:regulation/mifir-submissions">
                <MiFIROperationalPage page="submissions" />
              </Route>
              <Route path="/tables/:regulation/mifir-process-status">
                <MiFIRProcessStatusPage />
              </Route>
              <Route path="/tables/:regulation/:tableName">
                <TableDataPage />
              </Route>
              {auth.role === 'admin' ? (
                <Route path="/settings">
                  <Settings />
                </Route>
              ) : null}
              <Route path="/">
                <HomePage />
              </Route>
            </Switch>
          </Content>
          <Footer id="footer">Developed by Finmatek LTD | Version: 22.0.0</Footer>
        </Layout>
      </Layout>
      <Notification />
      <Redirect />
    </React.Fragment>
  )
};

const Item = ({ to, children, exact = false, ...props }) => (
  <Menu.Item {...props}>
    <NavLink
      className="nav-text"
      activeClassName="active"
      to={to}
      exact={exact}
    >
      {children}
    </NavLink>
  </Menu.Item>
);

const renderMenuItemChildren = ({ icon, title }) => (
  <React.Fragment>
    {icon}
    <span>
      {title}
    </span>
  </React.Fragment>
);

const renderMenuItem = (item, depth = 0) => {
  const depthClass = `menu-depth-${depth}`;
  const menuClassName = [depthClass, item.menuClassName].filter(Boolean).join(" ");

  return item.submenu ? (
    <SubMenu
      key={item.path}
      className={menuClassName}
      title={renderMenuItemChildren(item)}
    >
      {item.submenu.map(child => renderMenuItem(child, depth + 1))}
    </SubMenu>
  ) : (
    <Item
      key={item.path}
      className={menuClassName}
      to={item.path}
      exact={item.exact}
    >
      {item.children ? item.children : renderMenuItemChildren(item)}
    </Item>
  );
};
